import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = (d = {}) => ({ job_id: "", date: todayISO(), hours: "", rate: d.rate ?? "", person: d.person ?? "", description: "" });

export default function Time() {
  const { currentId, current } = useCompany();
  const [rows, setRows] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/time").then((r) => setRows(r.data));
  useEffect(() => {
    if (!currentId) return;
    load();
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, [currentId]);

  const selectedJob = jobs.find((j) => j.id === form.job_id) || null;
  const clientRates = (clients.find((c) => c.id === selectedJob?.client_id)?.rates) || [];
  const pickPerson = (name) => {
    const match = clientRates.find((r) => r.name === name);
    setForm((f) => ({ ...f, person: name, rate: match && match.rate != null ? String(match.rate) : f.rate }));
  };

  const openNew = () => {
    setForm(emptyForm({
      person: current?.default_timekeeper || "",
      rate: current?.default_rate ? String(current.default_rate) : "",
    }));
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (t) => {
    setForm({ job_id: t.job_id, date: t.date, hours: String(t.hours), rate: String(t.rate), person: t.person || "", description: t.description || "" });
    setEditId(t.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.job_id) return toast.error("Select a job");
    if (selectedJob && !selectedJob.client_id) return toast.error("This job has no client. Add a client to the job first.");
    if (!form.person) return toast.error("Select a person");
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0) return toast.error("Enter valid hours");
    const payload = { job_id: form.job_id, date: form.date, hours, rate: parseFloat(form.rate) || 0, person: form.person, description: form.description };
    try {
      if (editId) await api.put(`/time/${editId}`, payload);
      else await api.post("/time", payload);
      toast.success(editId ? "Time updated" : "Time logged");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save time entry");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this time entry?")) return;
    await api.delete(`/time/${id}`);
    load();
  };

  const totalHours = rows.reduce((s, r) => s + (r.hours || 0), 0);
  const totalCost = rows.reduce((s, r) => s + (r.cost || 0), 0);
  const costFactor = (current?.labour_cost_pct != null ? current.labour_cost_pct : 60) / 100;
  const previewBillable = (parseFloat(form.hours) || 0) * (parseFloat(form.rate) || 0);
  const previewCost = previewBillable * costFactor;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Time</h1>
          <p className="text-slate-500 text-sm mt-1">Log labour hours against a job — cost rolls into that job's profit.</p>
        </div>
        <Button data-testid="add-time-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> Log Time
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Total Hours</div>
          <div className="font-heading text-2xl font-bold tabular-nums">{totalHours.toFixed(2)}</div>
        </Card>
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Labour Cost</div>
          <div className="font-heading text-2xl font-bold tabular-nums text-amber-700">{money(totalCost)}</div>
        </Card>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {rows.length === 0 ? (
          <div className="p-16 text-center">
            <Clock className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No time logged yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="time-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Job</th>
                <th className="px-5 py-3 font-medium">Person</th>
                <th className="px-5 py-3 font-medium text-right">Hours</th>
                <th className="px-5 py-3 font-medium text-right">Rate</th>
                <th className="px-5 py-3 font-medium text-right">Cost</th>
                <th className="px-5 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-600">{fmtDate(t.date)}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    {t.job_name || "—"}
                    {t.description && <span className="block text-xs text-slate-400">{t.description}</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{t.person || "—"}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{(t.hours || 0).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(t.rate)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-amber-700">{money(t.cost)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" data-testid={`edit-time-${t.id}`} onClick={() => openEdit(t)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-time-${t.id}`} onClick={() => remove(t.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Time" : "Log Time"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Job</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="time-job-select" className="mt-1 rounded-md">
                  <SelectValue placeholder="Select a job" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <div className="mt-1"><DatePicker testId="time-date-picker" value={form.date} onChange={(d) => setForm({ ...form, date: d })} /></div>
              </div>
              <div>
                <Label>Person</Label>
                <Select value={form.person || undefined} onValueChange={pickPerson}>
                  <SelectTrigger data-testid="time-person-input" className="mt-1 rounded-md">
                    <SelectValue placeholder={selectedJob?.client_id ? "Select person" : "Pick a job with a client"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {clientRates.map((r) => (
                      <SelectItem key={r.name} value={r.name}>{r.name}{r.rate ? ` · $${r.rate}/hr` : ""}</SelectItem>
                    ))}
                    {form.person && !clientRates.some((r) => r.name === form.person) && (
                      <SelectItem value={form.person}>{form.person}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedJob && !selectedJob.client_id && (
                  <p className="text-xs text-amber-600 mt-1">This job has no client. Add one on the Jobs page.</p>
                )}
                {selectedJob?.client_id && clientRates.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No people on this client — add them on the Clients page.</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.25" data-testid="time-hours-input" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
              </div>
              <div>
                <Label>Rate ($/hr)</Label>
                <Input type="number" step="0.01" data-testid="time-rate-input" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="time-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div className="text-sm text-slate-500">
              Bills at <span className="font-medium text-slate-900 tabular-nums">{money(previewBillable)}</span>
              {" · "}labour cost <span className="font-medium text-amber-700 tabular-nums">{money(previewCost)}</span>
              <span className="text-slate-400"> ({current?.labour_cost_pct != null ? current.labour_cost_pct : 60}%)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-time-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
